/**
 * Visitor consent for analytics and marketing tracking.
 *
 * Model (built 2026-10-28 at Nic's direction — "no tracking before consent
 * where consent is legally required, especially EU / Norway"):
 * - EEA / UK / Switzerland / Norway visitors are OPT-IN: nothing tracks —
 *   not Google Tag Manager / GA4, not our first-party analytics, not the
 *   legacy session feed — until they accept. Region comes from Vercel's geo
 *   header (the `enzo_geo` cookie set in middleware.ts) or, as a fallback,
 *   the browser's time zone; either one pointing at a listed country is
 *   enough to require consent.
 * - Everyone else is OPT-OUT: analytics and marketing are on with notice and
 *   can be switched off any time at /privacy-choices/. Global Privacy Control
 *   always switches marketing (ad) tracking off.
 * - The choice is stored in the `enzo_consent` cookie for 12 months:
 *   "v1:a<0|1>:m<0|1>:<unix seconds>".
 *
 * The same decision runs first in an inline script in index.html (it has to,
 * so Google Consent Mode defaults are set before GTM can load). That script
 * publishes its result as window.__enzoConsent; keep the two in sync —
 * src/test/consent.test.ts checks the country list matches.
 */

export const CONSENT_COOKIE = "enzo_consent";
export const CONSENT_MAX_AGE_S = 365 * 24 * 60 * 60;
export const CONSENT_EVENT = "enzo:consent";

/** EU 27 + Iceland, Liechtenstein, Norway (EEA) + United Kingdom + Switzerland. */
export const CONSENT_COUNTRIES =
  "AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO GB CH".split(" ");
export const CONSENT_TIMEZONE_RE = /^(Europe\/|Atlantic\/(Reykjavik|Faroe|Canary|Madeira|Azores)|Arctic\/Longyearbyen)/;

export interface ConsentState {
  /** The visitor is in a region where tracking needs opt-in consent. */
  required: boolean;
  /** The visitor has made a choice (banner or preferences page). */
  decided: boolean;
  analytics: boolean;
  marketing: boolean;
}

type Gtag = (...args: unknown[]) => void;
type ConsentWindow = Window & {
  __enzoConsent?: ConsentState;
  __enzoLoadGtm?: () => void;
  gtag?: Gtag;
};

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

function readCookie(name: string): string {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : "";
  } catch { return ""; }
}

export function parseConsentCookie(value: string): { analytics: boolean; marketing: boolean } | null {
  const m = /^v1:a([01]):m([01]):\d+$/.exec(value);
  return m ? { analytics: m[1] === "1", marketing: m[2] === "1" } : null;
}

export function regionRequiresConsent(country: string, timeZone: string): boolean {
  return CONSENT_COUNTRIES.includes(country.toUpperCase()) || CONSENT_TIMEZONE_RE.test(timeZone);
}

/** Pure decision used by both the page and tests. */
export function decideConsent(input: { country: string; timeZone: string; cookie: string; gpc: boolean }): ConsentState {
  const required = regionRequiresConsent(input.country, input.timeZone);
  const stored = parseConsentCookie(input.cookie);
  return {
    required,
    decided: !!stored,
    analytics: stored ? stored.analytics : !required,
    marketing: (stored ? stored.marketing : !required) && !input.gpc,
  };
}

export function gpcEnabled(): boolean {
  return isBrowser() && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function computeConsent(): ConsentState {
  let timeZone = "";
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* old browser */ }
  return decideConsent({ country: readCookie("enzo_geo"), timeZone, cookie: readCookie(CONSENT_COOKIE), gpc: gpcEnabled() });
}

/** Current consent. Safe during SSR (returns "nothing granted"). */
export function getConsent(): ConsentState {
  if (!isBrowser()) return { required: true, decided: false, analytics: false, marketing: false };
  const w = window as ConsentWindow;
  if (!w.__enzoConsent) w.__enzoConsent = computeConsent();
  return w.__enzoConsent;
}

const granted = (v: boolean) => (v ? "granted" : "denied");

/** Saves the visitor's choice and applies it to every tracker on the page. */
export function setConsent(choice: { analytics: boolean; marketing: boolean }) {
  if (!isBrowser()) return;
  const w = window as ConsentWindow;
  const prev = getConsent();
  const next: ConsentState = {
    required: prev.required,
    decided: true,
    analytics: choice.analytics,
    marketing: choice.marketing && !gpcEnabled(),
  };
  w.__enzoConsent = next;
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    const value = `v1:a${next.analytics ? 1 : 0}:m${next.marketing ? 1 : 0}:${Math.floor(Date.now() / 1000)}`;
    document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${CONSENT_MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies blocked: the choice still applies to this page */ }

  try {
    w.gtag?.("consent", "update", {
      analytics_storage: granted(next.analytics),
      ad_storage: granted(next.marketing),
      ad_user_data: granted(next.marketing),
      ad_personalization: granted(next.marketing),
    });
    w.gtag?.("set", "ads_data_redaction", !next.marketing);
    if (next.analytics || next.marketing) w.__enzoLoadGtm?.();
  } catch { /* GTM not present (non-production host) */ }

  try { window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next })); } catch { /* old browser */ }
}

/** Runs `fn` whenever the visitor changes their choice. Returns an unsubscribe. */
export function onConsentChange(fn: (c: ConsentState) => void): () => void {
  if (!isBrowser()) return () => {};
  const handler = (e: Event) => fn((e as CustomEvent<ConsentState>).detail);
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}
